/**
 * Detail "Set crew…" — the outline bulk handler, run against a selection of one.
 *
 * The board's record ⌘K and its bulk ⌘K must offer one row the same actions, so this synthesises the
 * outline row the board would have handed over and calls `handleOutlineSetCrew` with it.
 */
import { computed, inject } from '@angular/core';
import { Entity } from '@jms/model';
import { EntityDataService, EntityDataServiceRaw } from '@workbuddy/services/entities';
import { ToastService } from '@workbuddy/ui/services';
import { WorkBuddyDialogService } from '@workbuddy/ui/v2/blocks/dialog';
import { BOARD_PANEL_HOST } from '@workbuddy/ui/v2/blocks/entity.board/components/board.panel/board.panel.host';
import { EntityBoardStore } from '@workbuddy/ui/v2/blocks/entity.board/state';
import type { ActionCommandCapability, ActionCommandContext, ActionCommandDescriptor, SurfaceContextLike } from '@workbuddy/ui/v2/surface.engine';
import { handleOutlineSetCrew, OUTLINE_SET_CREW_ACTION } from '../bulk/outline.bulk.crew';
import { OutlineRowPatcher } from '../bulk/outline.bulk.schedule';
import { NOOP_OUTLINE_ROW_PATCHER, reloadSurfaceRecord } from '../bulk/outline.record.command';
import { outlineRowForLine, singleOutlineCommandArgs } from '../bulk/outline.record';
import { OUTLINE_SET_CREW_LABEL } from '../relation/outline.relation.builder';
import { OutlineLineCommandConfig } from './outline.detail.command.config';

/** The two crew binds the dialog prefills from and writes back to. */
export interface OutlineCrewRecord {
  defaultContractor?: Entity;
  defaultResources?: Entity[];
}

export function createOutlineSetCrewCommand<TLine extends Entity & OutlineCrewRecord>(config: OutlineLineCommandConfig): ActionCommandCapability<TLine> {

  const dialogs = inject(WorkBuddyDialogService);
  const entityData = inject(EntityDataService);
  const entityDataRaw = inject(EntityDataServiceRaw);
  const toasts = inject(ToastService);
  const board = inject(EntityBoardStore, { optional: true });
  const panel = inject(BOARD_PANEL_HOST, { optional: true });

  return {
    descriptor: ({ page }: ActionCommandContext<TLine>) => computed<ActionCommandDescriptor>(() => ({
      id: OUTLINE_SET_CREW_ACTION,
      label: OUTLINE_SET_CREW_LABEL,
      iconLeft: 'fa-regular fa-helmet-safety',
      hidden: !page.$model()?._id || page.$readonly(),
      disabled: page.$saving(),
      pin: 'never',
    })),
    run: async (page: SurfaceContextLike<TLine>) => {

      const record = page.$model();
      const row = record && !page.$readonly() ? outlineRowForLine(record, config.kinds) : null;
      if (!row || !record) return;

      // The handler prefills the dialog from the row for a single-line selection; the Detail's own
      // crew fields live on the record, so the row carries neither and the dialog opens on the record
      // it is about to overwrite either way.
      const store: OutlineRowPatcher = board ?? NOOP_OUTLINE_ROW_PATCHER;
      const outcome = await handleOutlineSetCrew(singleOutlineCommandArgs(OUTLINE_SET_CREW_ACTION, { ...row, defaultContractor: record.defaultContractor, defaultResources: record.defaultResources } as typeof row), { kinds: config.kinds, dialogs, entityData, store, toasts });
      // `clearSelection: false` is how the handler says the dialog was dismissed or nothing applied.
      if (outcome.clearSelection === false) return;
      await reloadSurfaceRecord(entityDataRaw, config.entity, page, record._id);
      if (!board) panel?.refresh('collection');
    },
  };
}
